import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Grid,
  Image,
  Loader,
  NumberInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useDebounce } from '@ayocore/platform/client/hooks/useDebounce';

/**
 * ProductPicker (черновой/demо)
 *
 * Временный компонент для проверки UX подбора товаров в сделке.
 * Не является финальной реализацией и может меняться без сохранения текущего API.
 *
 * TODO: реализовать архитектурно корректную версию:
 * - вынести типы и API-клиент;
 * - разделить контейнер (данные/эффекты) и презентационный UI;
 * - добавить стабильный контракт;
 */
export type ProductSearchImage = {
  id?: string | number | null;
  url?: string | null;
  path?: string | null;
  documentId?: string | null;
};

type ProductSearchVariant = {
  external_images?: ProductSearchImage[] | null;
};

export type ProductPickerItem = {
  id?: string | number | null;
  name?: string | null;
  minPrice?: number | null;
  maxQty?: number | null;
  brand_name?: string | null;
  categories?: string[] | null;
  external_images?: ProductSearchImage[] | null;
  product_variants?: ProductSearchVariant[] | null;
};

export type ProductRowImageMetadata = {
  url: string;
  thumb_url: string | null;
  alt: string | null;
  source: 'grasias_catalog';
  external_id: string | number | null;
  path: string | null;
};

type ProductPickerPagination = {
  page: number;
  pageCount: number;
  total: number;
};

type ProductPickerResponse = {
  data?: ProductPickerItem[];
  meta?: {
    pagination?: Partial<ProductPickerPagination>;
  };
};

export interface ProductPickerProps {
  onProductsSelect?: (products: SelectedProductItem[]) => void;
  onClose?: () => void;
}

export type SelectedProductItem = {
  product: ProductPickerItem;
  quantity: number;
};

const GRASIAS_API_BASE = 'https://backend-api.grasias.ru';
const SEARCH_API_URL = `${GRASIAS_API_BASE}/api/search/products`;
const PAGE_SIZE = 20;
const MIN_QUERY_LENGTH = 2;
const PICKER_FIXED_HEIGHT = 700;
const RESULTS_AREA_HEIGHT = 560;
const EMPTY_PAGINATION: ProductPickerPagination = {
  page: 1,
  pageCount: 1,
  total: 0,
};

/** Строит URL картинки из локального path (приоритет) или внешнего url */
function buildImageUrl(image: ProductSearchImage | null | undefined): string | null {
  const path = toTrimmedString(image?.path);
  if (path) return `${GRASIAS_API_BASE}/uploads/${path}?width=600`;
  return toTrimmedString(image?.url);
}

function getProductImageUrl(product: ProductPickerItem): string | null {
  const images = [
    ...(product.external_images ?? []),
    ...(product.product_variants ?? []).flatMap(v => v.external_images ?? []),
  ];
  return buildImageUrl(images[0]);
}

function toTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function getProductMetadataImages(product: ProductPickerItem): ProductRowImageMetadata[] {
  const allImages: ProductSearchImage[] = [
    ...(product.external_images ?? []),
    ...(product.product_variants ?? []).flatMap(variant => variant.external_images ?? []),
  ];

  const seenUrls = new Set<string>();
  const alt = toTrimmedString(product.name);
  const result: ProductRowImageMetadata[] = [];

  for (const image of allImages) {
    const resolvedUrl = buildImageUrl(image);
    if (!resolvedUrl || seenUrls.has(resolvedUrl)) {
      continue;
    }

    seenUrls.add(resolvedUrl);
    result.push({
      url: resolvedUrl,
      thumb_url: resolvedUrl,
      alt,
      source: 'grasias_catalog',
      external_id: image?.id ?? null,
      path: toTrimmedString(image?.path),
    });
  }

  return result;
}

function formatRubPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) {
    return '—';
  }
  return `${price.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
}

function getProductKey(product: ProductPickerItem): string {
  if (product.id != null) {
    return `id:${String(product.id)}`;
  }
  return `name:${(product.name ?? '').trim().toLowerCase()}`;
}

function normalizeQuantity(value: string | number | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(1, Math.round(value));
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(1, Math.round(parsed));
    }
  }

  return 1;
}

/**
 * ProductPicker — тестовый поиск товаров по внешнему API Grasias.
 * Рендерит только контент, чтобы использоваться через useModal.
 */
export function ProductPicker({ onProductsSelect, onClose }: ProductPickerProps) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ProductPickerItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedProductItem[]>([]);
  const [draftQuantities, setDraftQuantities] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<ProductPickerPagination>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounce(query, 350);
  const normalizedQuery = debouncedQuery.trim();

  useEffect(() => {
    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setItems([]);
      setError(null);
      setLoading(false);
      setPagination(EMPTY_PAGINATION);
      return;
    }

    const abortController = new AbortController();
    const params = new URLSearchParams();
    params.set('q', normalizedQuery);
    params.set('pagination[page]', String(page));
    params.set('pagination[pageSize]', String(PAGE_SIZE));

    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const response = await fetch(`${SEARCH_API_URL}?${params.toString()}`, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Ошибка запроса: ${response.status}`);
        }

        const payload = (await response.json()) as ProductPickerResponse;
        const nextPagination = payload.meta?.pagination;
        setItems(Array.isArray(payload.data) ? payload.data : []);
        setPagination({
          page: Number(nextPagination?.page ?? page),
          pageCount: Number(nextPagination?.pageCount ?? 1),
          total: Number(nextPagination?.total ?? 0),
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        const message = err instanceof Error ? err.message : String(err);
        setItems([]);
        setPagination(EMPTY_PAGINATION);
        setError(message);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [normalizedQuery, page]);

  const isSelected = (product: ProductPickerItem): boolean => {
    const key = getProductKey(product);
    return selectedItems.some(item => getProductKey(item.product) === key);
  };

  const handleSelectProduct = (product: ProductPickerItem) => {
    const key = getProductKey(product);
    const quantity = normalizeQuantity(draftQuantities[key]);

    setSelectedItems(prev => {
      if (prev.some(item => getProductKey(item.product) === key)) {
        return prev;
      }
      return [...prev, { product, quantity }];
    });
  };

  const handleRemoveSelected = (product: ProductPickerItem) => {
    const key = getProductKey(product);
    setSelectedItems(prev =>
      prev.filter(item => {
        return getProductKey(item.product) !== key;
      })
    );
  };

  const handleDone = () => {
    if (onProductsSelect) {
      onProductsSelect(selectedItems);
    }
  };

  return (
    <Stack gap='sm'>
      <Grid gutter='md' align='stretch' style={{ height: PICKER_FIXED_HEIGHT }}>
        <Grid.Col span={8}>
          <Stack gap='xs' h='100%' style={{ minHeight: 0 }}>
            <TextInput
              size='xs'
              value={query}
              onChange={event => {
                setQuery(event.currentTarget.value);
                setPage(1);
              }}
              leftSection={<IconSearch size={14} />}
              placeholder='Введите запрос, например: футболки'
            />

            <Text size='xs' c='dimmed'>
              Тестовый поиск по внешнему API каталога.
            </Text>

            {error && (
              <Alert color='red' title='Ошибка поиска'>
                <Text size='xs'>{error}</Text>
              </Alert>
            )}

            <Card withBorder padding='xs' h={RESULTS_AREA_HEIGHT} style={{ overflow: 'hidden' }}>
              {normalizedQuery.length < MIN_QUERY_LENGTH ? (
                <Group h='100%' justify='center' align='center'>
                  <Text size='xs' c='dimmed'>
                    Введите минимум {MIN_QUERY_LENGTH} символа, чтобы загрузить товары.
                  </Text>
                </Group>
              ) : loading ? (
                <Group h='100%' justify='center' align='center'>
                  <Loader size='sm' />
                </Group>
              ) : items.length === 0 ? (
                <Group h='100%' justify='center' align='center'>
                  <Text size='xs' c='dimmed'>
                    По вашему запросу ничего не найдено.
                  </Text>
                </Group>
              ) : (
                <ScrollArea h='100%' offsetScrollbars scrollbarSize={8} scrollHideDelay={0}>
                  <Stack gap='xs' pr={4}>
                    {items.map(product => {
                      const productKey = getProductKey(product);
                      const imageUrl = getProductImageUrl(product);
                      const category = product.categories?.[0];
                      const idLabel = product.id == null ? '—' : String(product.id);
                      const selected = isSelected(product);
                      const quantity = draftQuantities[productKey] ?? 1;
                      return (
                        <Card
                          key={`${idLabel}-${product.name ?? 'product'}`}
                          withBorder
                          padding='xs'
                          style={{ flexShrink: 0 }}
                        >
                          <Group align='flex-start' wrap='nowrap' gap='sm'>
                            {imageUrl ? (
                              <Image
                                src={imageUrl}
                                alt={product.name ?? 'Товар'}
                                w={72}
                                h={72}
                                radius='sm'
                                fit='cover'
                              />
                            ) : (
                              <Group
                                w={72}
                                h={72}
                                justify='center'
                                align='center'
                                style={{
                                  border: '1px solid var(--mantine-color-gray-4)',
                                  borderRadius: 8,
                                  flexShrink: 0,
                                }}
                              >
                                <Text size='xs' c='dimmed'>
                                  Нет фото
                                </Text>
                              </Group>
                            )}

                            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                              <Text size='sm' fw={600} lineClamp={2}>
                                {product.name || 'Без названия'}
                              </Text>
                              <Group gap='xs'>
                                <Badge size='xs' variant='light'>
                                  ID: {idLabel}
                                </Badge>
                                <Badge size='xs' variant='light' color='teal'>
                                  от {formatRubPrice(product.minPrice)}
                                </Badge>
                                {product.maxQty != null && (
                                  <Badge size='xs' variant='light' color='green'>
                                    Остаток: {Number(product.maxQty)}
                                  </Badge>
                                )}
                              </Group>
                              <Group gap='md'>
                                {category && (
                                  <Text size='xs' c='dimmed'>
                                    Категория: {category}
                                  </Text>
                                )}
                                {product.brand_name && (
                                  <Text size='xs' c='dimmed'>
                                    Бренд: {product.brand_name}
                                  </Text>
                                )}
                              </Group>
                              <Group justify='flex-end' mt={2}>
                                <NumberInput
                                  size='xs'
                                  value={quantity}
                                  min={1}
                                  step={1}
                                  allowDecimal={false}
                                  clampBehavior='strict'
                                  w={96}
                                  placeholder='Кол-во'
                                  disabled={selected}
                                  onChange={value => {
                                    setDraftQuantities(prev => ({
                                      ...prev,
                                      [productKey]: normalizeQuantity(value),
                                    }));
                                  }}
                                />
                                <Button
                                  size='xs'
                                  variant={selected ? 'default' : 'light'}
                                  onClick={() => handleSelectProduct(product)}
                                  disabled={selected}
                                >
                                  {selected ? 'Добавлено' : 'Добавить'}
                                </Button>
                              </Group>
                            </Stack>
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </Card>

            <Group justify='space-between' mt={2} style={{ minHeight: 32 }}>
              {pagination.pageCount > 1 ? (
                <>
                  <Text size='xs' c='dimmed'>
                    Страница {pagination.page} из {pagination.pageCount}
                    {' • '}
                    Всего: {pagination.total}
                  </Text>
                  <Group gap='xs'>
                    <Button
                      size='xs'
                      variant='default'
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={loading || pagination.page <= 1}
                    >
                      Назад
                    </Button>
                    <Button
                      size='xs'
                      variant='default'
                      onClick={() => setPage(prev => Math.min(pagination.pageCount, prev + 1))}
                      disabled={loading || pagination.page >= pagination.pageCount}
                    >
                      Вперёд
                    </Button>
                  </Group>
                </>
              ) : (
                <Text size='xs' c='transparent'>
                  .
                </Text>
              )}
            </Group>
          </Stack>
        </Grid.Col>

        <Grid.Col span={4}>
          <Stack gap='xs' h='100%'>
            <Group justify='space-between'>
              <Text size='sm' fw={600}>
                Выбранные товары
              </Text>
              <Badge size='sm' variant='light'>
                {selectedItems.length}
              </Badge>
            </Group>
            <Divider />

            {selectedItems.length === 0 ? (
              <Text size='xs' c='dimmed'>
                Нажмите «Добавить» на товаре слева, чтобы добавить его сюда.
              </Text>
            ) : (
              <ScrollArea
                h='100%'
                style={{ minHeight: 0, flex: 1 }}
                offsetScrollbars
                scrollbarSize={8}
                scrollHideDelay={0}
              >
                <Stack gap='xs' pr={4}>
                  {selectedItems.map(selectedItem => {
                    const { product, quantity } = selectedItem;
                    const imageUrl = getProductImageUrl(product);
                    const idLabel = product.id == null ? '—' : String(product.id);
                    return (
                      <Card
                        key={`selected-${idLabel}-${product.name ?? 'product'}`}
                        withBorder
                        padding='xs'
                        style={{ flexShrink: 0 }}
                      >
                        <Group align='flex-start' wrap='nowrap' gap='sm'>
                          {imageUrl ? (
                            <Image
                              src={imageUrl}
                              alt={product.name ?? 'Товар'}
                              w={56}
                              h={56}
                              radius='sm'
                              fit='cover'
                            />
                          ) : (
                            <Group
                              w={56}
                              h={56}
                              justify='center'
                              align='center'
                              style={{
                                border: '1px solid var(--mantine-color-gray-4)',
                                borderRadius: 8,
                                flexShrink: 0,
                              }}
                            >
                              <Text size='xs' c='dimmed'>
                                Нет фото
                              </Text>
                            </Group>
                          )}

                          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                            <Text size='xs' fw={600} lineClamp={2}>
                              {product.name || 'Без названия'}
                            </Text>
                            <Group gap={4}>
                              <Badge size='xs' variant='light'>
                                ID: {idLabel}
                              </Badge>
                              <Badge size='xs' variant='light' color='teal'>
                                {formatRubPrice(product.minPrice)}
                              </Badge>
                              <Badge size='xs' variant='light' color='blue'>
                                Кол-во: {quantity}
                              </Badge>
                            </Group>
                            <Group justify='flex-end'>
                              <Button
                                size='compact-xs'
                                variant='subtle'
                                color='red'
                                onClick={() => handleRemoveSelected(product)}
                              >
                                Убрать
                              </Button>
                            </Group>
                          </Stack>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              </ScrollArea>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <Group justify='flex-end'>
        <Button size='xs' variant='default' onClick={() => onClose?.()}>
          Отмена
        </Button>
        <Button size='xs' onClick={handleDone} disabled={selectedItems.length === 0}>
          Готово
        </Button>
      </Group>
    </Stack>
  );
}

export default ProductPicker;
